import { createFileRoute, Link } from "@tanstack/react-router";
import { Bell, Check, ExternalLink, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { fetchWithAuth } from "@/lib/auth-client";
import type { NotificationDto, NotificationListMeta } from "@procureflow/shared-types";
import { PageHeader } from "@/components/page-header";
import { notificationLink } from "@/lib/notification-link";

// US-027 — notifications inbox feed.
//
// Lists the authenticated user's notifications (sorted newest-first),
// supports per-row mark-read and a global mark-all-read action. Header
// bell badge stays in sync via the `notifications:changed` custom event.

export const Route = createFileRoute("/notifications")({
  head: () => ({
    meta: [
      { title: "Notifications — ProcureFlow" },
      { name: "description", content: "Procurement activity surfaces here as it happens." },
    ],
  }),
  component: Page,
});

function emitNotificationsChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("notifications:changed"));
  }
}

function Page() {
  const [items, setItems] = useState<NotificationDto[]>([]);
  const [meta, setMeta] = useState<NotificationListMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function refetch() {
    setError(null);
    try {
      const res = await fetchWithAuth("/api/notifications");
      if (!res.ok) {
        setError("Failed to load notifications.");
        return;
      }
      const body = (await res.json()) as {
        data: NotificationDto[];
        meta: NotificationListMeta;
      };
      setItems(body.data);
      setMeta(body.meta);
    } catch {
      setError("An unexpected error occurred.");
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      setLoading(true);
      await refetch();
      if (!cancelled) setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // US-078 (ADR-022) — live-prepend a pushed notification (broadcast by the
  // header's SSE connection) without a refetch. Dedup by id; bump unread.
  useEffect(() => {
    function onNew(e: Event) {
      const n = (e as CustomEvent<NotificationDto>).detail;
      if (!n) return;
      setItems((prev) => (prev.some((x) => x.id === n.id) ? prev : [n, ...prev]));
      setMeta((m) =>
        m ? { ...m, total: m.total + 1, unread_count: m.unread_count + (n.is_read ? 0 : 1) } : m,
      );
    }
    if (typeof window === "undefined") return;
    window.addEventListener("notification:new", onNew as EventListener);
    return () => window.removeEventListener("notification:new", onNew as EventListener);
  }, []);

  async function handleMarkRead(id: string) {
    setBusyId(id);
    try {
      const res = await fetchWithAuth(`/api/notifications/${id}/read`, {
        method: "POST",
      });
      if (res.ok) {
        await refetch();
        emitNotificationsChanged();
      }
    } finally {
      setBusyId(null);
    }
  }

  async function handleMarkAllRead() {
    setBusyId("__all__");
    try {
      const res = await fetchWithAuth(`/api/notifications/read-all`, {
        method: "POST",
      });
      if (res.ok) {
        await refetch();
        emitNotificationsChanged();
      }
    } finally {
      setBusyId(null);
    }
  }

  const hasItems = items.length > 0;
  const unreadCount = meta?.unread_count ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        description="Procurement activity surfaces here as it happens."
        action={
          hasItems ? (
            <button
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0 || busyId === "__all__"}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3.5 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="h-4 w-4" /> Mark all read
            </button>
          ) : null
        }
      />

      {loading ? (
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center">
          <p className="text-sm text-muted-foreground">Loading…</p>
        </div>
      ) : error ? (
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      ) : !hasItems ? (
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10">
          <div className="flex items-start gap-4 max-w-xl">
            <div className="h-10 w-10 rounded-xl bg-muted text-muted-foreground grid place-items-center shrink-0">
              <Bell className="h-5 w-5" />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">You have no notifications yet.</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Approvals, status changes and deadlines appear here as procurement activity happens.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl bg-card border border-border shadow-elegant divide-y divide-border">
          {items.map((n) => (
            <div
              key={n.id}
              data-testid={`notif-row-${n.id}`}
              className={`flex items-start gap-4 p-5 hover:bg-accent/40 transition ${
                !n.is_read ? "bg-primary/[0.03]" : ""
              }`}
            >
              <div
                className={`h-10 w-10 rounded-xl grid place-items-center shrink-0 ${
                  !n.is_read ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                }`}
              >
                <Bell className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm">{n.type}</p>
                  {n.channel === "email" && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                      title="Email notification sent (Phase 1: MSW mock)"
                    >
                      <Mail className="h-2.5 w-2.5" /> email sent
                    </span>
                  )}
                </div>
                <p className="text-sm text-foreground mt-0.5">{n.message}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  <span>{n.created_at}</span>
                  {(() => {
                    const target = notificationLink(n);
                    if (!target) return null;
                    // US-015 — clicking the notification navigates to
                    // the linked entity. Rendered inline so the row's
                    // mark-read button stays distinct.
                    return (
                      <Link
                        to={target}
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        Open <ExternalLink className="h-3 w-3" />
                      </Link>
                    );
                  })()}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!n.is_read && (
                  <>
                    <span className="h-2 w-2 rounded-full bg-primary" />
                    <button
                      data-testid={`notif-mark-read-${n.id}`}
                      onClick={() => handleMarkRead(n.id)}
                      disabled={busyId === n.id}
                      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium hover:bg-accent disabled:opacity-50"
                      aria-label="Mark as read"
                    >
                      <Check className="h-3 w-3" /> Mark read
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
